import { spacing } from "@procore/core-react";

// Position presets for common window placements
export type PositionPreset = "center" | "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

// Element ID-based positioning (for Redux state)
export interface RelativeToElementId {
    elementId: string;
    placement?: "right" | "left" | "top" | "bottom";
    offset?: { x?: number; y?: number };
    align?: {
        horizontal?: "left" | "right" | "center";
        vertical?: "top" | "bottom" | "center";
    };
}

export type WindowPosition = PositionPreset | { x: number; y: number } | RelativeToElementId;

/**
 * Calculate window position from preset, coordinates, or element-relative positioning.
 *
 * All return values are in **container-local** coordinates (i.e. relative to the
 * render-container's top-left corner, not the browser viewport).
 *
 * @param position - Position configuration (undefined defaults to center)
 * @param windowSize - Window dimensions
 * @param viewport - Container dimensions and its optional offset within the browser viewport.
 * Defaults to the full browser viewport with no offset.
 * @returns Calculated x,y coordinates in container-local space
 */
export function calculateWindowPosition(
    position: WindowPosition | undefined,
    windowSize: { width: number; height: number },
    viewport: { width: number; height: number; left?: number; top?: number } = {
        width: window.innerWidth,
        height: window.innerHeight,
    },
): { x: number; y: number } {
    // Default to center if no position specified
    if (!position || position === "center") {
        return {
            x: Math.max(0, (viewport.width - windowSize.width) / 2),
            y: Math.max(0, (viewport.height - windowSize.height) / 2),
        };
    }

    // Handle preset positions
    if (typeof position === "string") {
        const margin = spacing.sm; // Standard margin from viewport edges
        const presets: Record<PositionPreset, { x: number; y: number }> = {
            center: {
                x: Math.max(margin, (viewport.width - windowSize.width) / 2),
                y: Math.max(margin, (viewport.height - windowSize.height) / 2),
            },
            topLeft: { x: margin, y: margin },
            topRight: { x: Math.max(margin, viewport.width - windowSize.width - margin), y: margin },
            bottomLeft: { x: margin, y: Math.max(margin, viewport.height - windowSize.height - margin) },
            bottomRight: {
                x: Math.max(margin, viewport.width - windowSize.width - margin),
                y: Math.max(margin, viewport.height - windowSize.height - margin),
            },
        };
        return presets[position] || presets.center;
    }

    // Handle direct coordinates
    if ("x" in position && "y" in position) {
        // If coordinates are NaN, fall back to center positioning
        if (isNaN(position.x) || isNaN(position.y)) {
            return {
                x: Math.max(0, (viewport.width - windowSize.width) / 2),
                y: Math.max(0, (viewport.height - windowSize.height) / 2),
            };
        }

        return {
            x: Math.max(0, Math.min(position.x, viewport.width - windowSize.width)),
            y: Math.max(0, Math.min(position.y, viewport.height - windowSize.height)),
        };
    }

    // Handle element-relative positioning
    const elementToUse = document.getElementById(position.elementId);

    if (elementToUse) {
        const containerLeft = viewport.left ?? 0;
        const containerTop = viewport.top ?? 0;

        const clientRect = elementToUse.getBoundingClientRect();
        const rect = {
            left: clientRect.left - containerLeft,
            top: clientRect.top - containerTop,
            right: clientRect.right - containerLeft,
            bottom: clientRect.bottom - containerTop,
            width: clientRect.width,
            height: clientRect.height,
        };

        const { placement = "right", offset = {}, align = {} } = position;

        const defaultOffsets = {
            right: { x: spacing.sm, y: 0 },
            left: { x: spacing.sm, y: 0 },
            top: { x: 0, y: spacing.sm },
            bottom: { x: 0, y: spacing.sm },
        };

        const defaults = defaultOffsets[placement];
        const offsetX = offset.x ?? defaults.x;
        const offsetY = offset.y ?? defaults.y;

        let x: number;
        let y: number;

        switch (placement) {
            case "right":
                x = rect.right + offsetX;
                y = rect.top + offsetY;
                break;
            case "left":
                x = rect.left - windowSize.width - offsetX;
                y = rect.top + offsetY;
                break;
            case "top":
                x = rect.left + offsetX;
                y = rect.top - windowSize.height - offsetY;
                break;
            case "bottom":
                x = rect.left + offsetX;
                y = rect.bottom + offsetY;
                break;
            default:
                x = rect.right + offsetX;
                y = rect.top + offsetY;
        }

        if (align.horizontal) {
            switch (align.horizontal) {
                case "right":
                    x = rect.right - windowSize.width;
                    break;
                case "center":
                    x = rect.left + (rect.width - windowSize.width) / 2;
                    break;
                case "left":
                    x = rect.left;
                    break;
            }
            if (placement === "right") {
                x += offsetX;
            }
            if (placement === "left") {
                x -= offsetX;
            }
            if (placement === "top" || placement === "bottom") {
                x += offsetX;
            }
        }

        if (align.vertical) {
            switch (align.vertical) {
                case "bottom":
                    y = rect.bottom - windowSize.height;
                    break;
                case "center":
                    y = rect.top + (rect.height - windowSize.height) / 2;
                    break;
                case "top":
                    y = rect.top;
                    break;
            }
            if (placement === "bottom") {
                y += offsetY;
            }
            if (placement === "top") {
                y -= offsetY;
            }
            if (placement === "left" || placement === "right") {
                y += offsetY;
            }
        }

        // Apply viewport bounds checking
        return {
            x: Math.max(0, Math.min(x, viewport.width - windowSize.width)),
            y: Math.max(0, Math.min(y, viewport.height - windowSize.height)),
        };
    }

    // Fallback to center for any invalid position
    return {
        x: Math.max(0, (viewport.width - windowSize.width) / 2),
        y: Math.max(0, (viewport.height - windowSize.height) / 2),
    };
}
